import { h } from 'preact'
import { useEffect, useState, useRef, useCallback } from 'preact/hooks'
import { format, previousMonday, startOfWeek } from 'date-fns'
import { PreviousMonthButton } from './WeekPicker/PreviousMonthButton'
import { NextMonthButton } from './WeekPicker/NextMonthButton'
import { WeekRow } from './WeekPicker/WeekRow'
import { Turbo } from '@hotwired/turbo-rails'

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTH_NAMES: string[] = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const DATE_FORMAT = 'dd MMM yy'

function formatDate(date: Date) {
  return format(date, DATE_FORMAT)
}

function groupArray<T>(array: T[], num: number): T[][] {
  const group: T[][] = []

  for (let i = 0; i < array.length; i += num) {
    group.push(array.slice(i, i + num))
  }

  return group
}

function useOutsideClick(ref: any, onClickOut: () => void, deps: any[] = []) {
  useEffect(() => {
    const onClick = ({ target }: any) => {
      !ref?.contains(target) && onClickOut?.()
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, deps)
}

interface Props {
  date?: Date
}

export interface DayObject {
  day: number
  type: 'blank' | 'normal'
}

export function WeekPicker({ date: initialDate = new Date() }: Props) {
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState(initialDate)
  const [month, setMonth] = useState(initialDate.getMonth())
  const [year, setYear] = useState(initialDate.getFullYear())
  const [blankDaysInMonth, setBlankDaysInMonth] = useState<number[]>([])
  const [daysInMonth, setDaysInMonth] = useState<number[]>([])

  const containerRef = useRef<HTMLDivElement>()
  const today = useRef(new Date()).current

  const formattedDate = formatDate(date)
  const formattedEndDate = formatDate(
    new Date(date.getTime() + 6 * 24 * 60 * 60 * 1000)
  )
  const allDays: DayObject[] = [
    ...blankDaysInMonth.map(day => ({
      day,
      type: 'blank',
    })),
    ...daysInMonth.map(day => ({
      day,
      type: 'normal',
    })),
  ]
  const groupedDays = groupArray(allDays, 7)
  const atMaxMonth = today.getFullYear() === year && today.getMonth() === month

  useEffect(() => {
    document.addEventListener('keyup', closeWithKeyboard)

    return () => {
      document.removeEventListener('keyup', closeWithKeyboard)
    }
  }, [])

  useOutsideClick(
    containerRef.current,
    () => {
      setOpen(false)
    },
    [containerRef.current]
  )

  const toggleOpen = useCallback(() => {
    setOpen((currentValue: boolean) => !currentValue)
  }, [])

  function datePickerCalculateDays() {
    const totalDaysInMonth = new Date(year, month + 1, 0).getDate()
    // find where to start calendar day of week
    const dayOfWeek = new Date(year, month).getDay()
    const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1
    const blankDaysArray: number[] = []

    for (var i = 1; i <= dayIndex; i++) {
      blankDaysArray.push(i)
    }

    const daysArray: number[] = []
    for (var i = 1; i <= totalDaysInMonth; i++) {
      daysArray.push(i)
    }

    setBlankDaysInMonth(blankDaysArray)
    setDaysInMonth(daysArray)
  }

  useEffect(() => {
    datePickerCalculateDays()
  }, [date])

  useEffect(() => {
    datePickerCalculateDays()
  }, [month])

  const closeWithKeyboard = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false)
    }
  }, [])

  const selectThisWeek = useCallback(() => {
    const today = new Date()
    const monday = startOfWeek(today, { weekStartsOn: 1 })

    dateSelected(monday)
  }, [])

  const selectLastWeek = useCallback(() => {
    const today = new Date()
    const monday = startOfWeek(today, { weekStartsOn: 1 })
    const lastMonday = previousMonday(monday)

    dateSelected(lastMonday)
  }, [])

  function dateSelected(selectedDate: Date): void {
    setDate(selectedDate)
    setOpen(false)

    const url = new URL(window.location.href)
    url.searchParams.set('week_commencing', format(selectedDate, 'yyyy-MM-dd'))
    Turbo.visit(url, {
      action: 'replace',
    })
  }

  function datePickerDayClicked(day: number) {
    const selectedDate = new Date(year, month, day)

    setDate(selectedDate)
    setOpen(false)

    const url = new URL(window.location.href)
    url.searchParams.set('week_commencing', format(selectedDate, 'yyyy-MM-dd'))
    Turbo.visit(url, {
      action: 'replace',
    })
  }

  function datePickerPreviousMonth() {
    if (month === 0) {
      setYear(year - 1)
      setMonth(11)
    } else {
      setMonth(month - 1)
    }
  }

  function datePickerNextMonth() {
    if (atMaxMonth) return

    if (month === 11) {
      setMonth(0)
      setYear(year + 1)
    } else {
      setMonth(month + 1)
    }
  }

  return (
    <div ref={containerRef} className="flex relative items-center">
      <button type="button" className="btn-sm-secondary" onClick={toggleOpen}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          className="mr-2 w-5 h-5"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
          />
        </svg>
        {formattedDate} - {formattedEndDate}
      </button>

      {open && (
        <div className="absolute top-0 left-0 p-4 mt-12 max-w-lg antialiased rounded-lg border shadow bg-card w-68 border-border">
          <div className="flex justify-between items-center mb-2">
            <div>
              <span className="text-lg font-bold text-foreground">
                {MONTH_NAMES[month]}
              </span>
              <span className="ml-1 text-lg font-normal text-foreground">
                {year}
              </span>
            </div>
            <div>
              <PreviousMonthButton onPress={datePickerPreviousMonth} />
              <NextMonthButton
                onPress={datePickerNextMonth}
                disabled={atMaxMonth}
              />
            </div>
          </div>
          <div className="grid grid-cols-7 mb-3">
            {DAY_NAMES.map(day => (
              <div key={day} className="px-0.5">
                <div className="text-xs font-medium text-center text-foreground">
                  {day}
                </div>
              </div>
            ))}
          </div>
          {groupedDays.map(group => (
            <WeekRow
              key={group}
              date={date}
              week={group}
              month={month}
              year={year}
              onClick={datePickerDayClicked}
            />
          ))}
          <div className="flex flex-1 items-center mt-2 space-x-2">
            <button
              type="button"
              className="flex-1 btn-secondary"
              onClick={selectLastWeek}
            >
              Last week
            </button>
            <button
              type="button"
              className="flex-1 btn-secondary"
              onClick={selectThisWeek}
            >
              This week
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
